import { api_verification_post } from 'api'
import { Timer } from 'comps/helpers'
import { Component, createEffect, on, onCleanup, Show } from 'solid-js'
import { produce } from 'solid-js/store'
import {
    getStageClassList,
    LoginStages,
    lstate,
    numDigits,
    setlState,
    user_login,
} from './shared'

import { EnvelopIcon, GoBackIcon, WarnigIcon } from 'icons/login'
import { convertPersianPriceToNumber } from 'shared'
import { addAlert } from 'store/user'
import './style/code.scss'

let timer: ReturnType<typeof setInterval> | undefined
let expiresAt = 0

function clear_expire_timer() {
    if (timer !== undefined) {
        clearInterval(timer)
        timer = undefined
    }

    expiresAt = 0
}

export function set_expire() {
    clear_expire_timer()

    expiresAt = Date.now() + lstate.expires * 1000

    const update = () => {
        const remaining = Math.max(
            0,
            Math.ceil((expiresAt - Date.now()) / 1000)
        )

        setlState(
            produce(state => {
                state.expires = remaining

                if (
                    remaining === 0 &&
                    !state.loading &&
                    !state.canResend &&
                    state.stage === LoginStages.CODE_SECTION
                ) {
                    state.error = 'محلت تایید کد تموم شد.'
                }
            })
        )

        if (remaining === 0) {
            clear_expire_timer()
        }
    }

    update()

    if (expiresAt > Date.now()) {
        timer = setInterval(update, 1000)
    }
}

export const CodeStage: Component = () => {
    let inputRef: HTMLInputElement

    createEffect(
        on(
            () => lstate.stage === LoginStages.CODE_SECTION,
            s => {
                if (!s) {
                    inputRef.blur()
                    setlState('expires', -1)
                    return
                }

                if (inputRef) {
                    inputRef.focus()
                }

                onCleanup(() => {
                    // inputRef.blur()
                    clear_expire_timer()
                    setlState({ loading: false })
                })
            }
        )
    )

    createEffect(() => {
        if (lstate.stage !== LoginStages.CODE_SECTION || !inputRef) return

        if (!('OTPCredential' in window)) return

        let ac = new AbortController()

        const form = inputRef.closest('form')

        if (form) {
            form.addEventListener(
                'submit',
                () => {
                    // Cancel the WebOTP API.
                    ac.abort('changed')
                },
                {
                    passive: true,
                }
            )
        }
        // Invoke the WebOTP API
        navigator.credentials
            .get({
                // @ts-ignore
                otp: { transport: ['sms'] },
                signal: ac.signal,
            })
            .then(otp => {
                // @ts-ignore
                let code = otp.code

                setlState({
                    code,
                })
                user_login()
            })
            .catch(err => {
                console.log(err)
            })

        onCleanup(() => {
            form?.removeEventListener('submit', () => {
                ac.abort()
            })
            ac.abort('cleanup')
        })
    })

    async function resend_code() {
        setlState({ loading: true })

        let res = await api_verification_post({
            action: 'login',
            telephone: {
                cc: 98,
                phone: lstate.phone,
            },
        })

        setlState({ loading: false, error: '' })

        if (!res.ok()) {
            addAlert({
                timeout: 3,
                type: 'error',
                subject: 'یه بار دیگه تلاش کن',
            })

            return
        }

        const nextResend = res.body.next_resend || false

        if (!nextResend) {
            if (res.body.verify_expires > 0 && res.body.last_code_expires > 0) {
                setlState({
                    expires: res.body.last_code_expires,
                    canResend: false,
                })

                set_expire()

                return
            }

            addAlert({
                subject: 'لطفا چند دقیقه دیگه دوباره تلاش کن',
                timeout: 3,
                type: 'error',
            })

            return
        }

        setlState({
            expires: nextResend,
            canResend: true,
        })

        set_expire()
    }

    const goBackToPhone = () => {
        setlState({
            stage: LoginStages.PHONE_SECTION,
            phone: '',
            error: '',
            code: '',
        })
        if (timer) {
            clearTimeout(timer)
        }
    }

    function handleInput(
        e: InputEvent & {
            currentTarget: HTMLInputElement
            target: HTMLInputElement
        }
    ) {
        e.currentTarget.style.setProperty(
            '--_otp-digit',
            String(e.currentTarget.selectionStart)
        )

        if (lstate.error) {
            setlState({ error: '' })
        }

        const digitsOnly = convertPersianPriceToNumber(e.currentTarget.value)

        e.currentTarget.value = digitsOnly

        if (e.currentTarget.value.length == numDigits) {
            setlState({
                code: digitsOnly,
            })
            user_login()
        }
    }

    return (
        <div
            class='code-stage'
            id='stage'
            classList={getStageClassList(LoginStages.CODE_SECTION)}
        >
            <div
                class='svg-section-container'
                classList={{
                    active: getStageClassList(LoginStages.CODE_SECTION).active,
                }}
            >
                <div class='svg-container'>
                    <img
                        src='/public/svgs/mascot/phone.svg'
                        loading='lazy'
                        decoding='async'
                        class='svg-wrapper'
                    />

                    <div class='icon right'>
                        <EnvelopIcon />
                    </div>
                    <div class='icon left'>
                        <EnvelopIcon />
                    </div>
                </div>
            </div>

            <div class='code-stage-wrapper'>
                <div class='login-stage-header-wrapper'>
                    <div class='header'>
                        <h2 class='title'>برات کد فرستادم!</h2>
                        <p class='title_smaller desc'>
                            کد 5 رقمی برای شماره {lstate.phone} ارسال شد.
                        </p>
                        <button
                            type={'button'}
                            class='basic-button title_smaller change-number'
                            onclick={() => {
                                goBackToPhone()
                            }}
                        >
                            ویرایش شماره موبایل
                        </button>
                    </div>
                    <button
                        onclick={() => {
                            goBackToPhone()
                        }}
                        class='go-back'
                        name='go-back'
                        type={'button'}
                    >
                        <GoBackIcon />
                    </button>
                </div>

                <div class='code-input-container'>
                    <input
                        disabled={lstate.stage != LoginStages.CODE_SECTION}
                        name='otp'
                        id='otp'
                        aria-label='one-time-code'
                        type='text'
                        autocomplete='one-time-code'
                        pattern={`\\d{${numDigits}}`}
                        inputMode='numeric'
                        class='section_title'
                        ref={e => (inputRef = e)}
                        maxLength={numDigits}
                        oninput={handleInput}
                        value={lstate.code}
                        onInvalid={e => {
                            e.preventDefault()

                            setlState({ error: 'کد را به درستی وارد کنید!' })
                        }}
                    />
                    <div class='error-wrapper'>
                        <div class='error title_smaller'>
                            <Show when={lstate.error}>
                                <>
                                    <WarnigIcon />
                                    {lstate.error}
                                </>
                            </Show>
                        </div>
                    </div>

                    <button
                        class='resend title_small'
                        type={'button'}
                        classList={{
                            disable: lstate.expires >= 1 || !lstate.canResend,
                            hide: lstate.expires == -1,
                        }}
                        onclick={() => {
                            if (lstate.expires >= 1) return

                            resend_code()
                        }}
                    >
                        <Show when={lstate.expires > 0}>
                            <Timer seconds={lstate.expires} />
                        </Show>
                        ارسال دوباره
                    </button>
                </div>
            </div>
        </div>
    )
}
